import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/hooks/useAuth";

import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import PageLoader from "@/components/PageLoader";
import RouteProgress from "@/components/RouteProgress";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import Donate from "@/pages/Donate";
import DonateSuccess from "@/pages/DonateSuccess";
import Contact from "@/pages/Contact";
import PrayerTimesPage from "@/pages/PrayerTimesPage";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DynamicPage from "@/pages/DynamicPage";
import Gallery from "@/pages/Gallery";
import FacilityRentals from "@/pages/FacilityRentals";
import Membership from "@/pages/Membership";
import MessageOfSubmission from "@/pages/MessageOfSubmission";
import CommunityResources from "@/pages/CommunityResources";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminOverview from "@/pages/admin/AdminOverview";
import EventsAdmin from "@/pages/admin/EventsAdmin";
import ServicesAdmin from "@/pages/admin/ServicesAdmin";
import PagesAdmin from "@/pages/admin/PagesAdmin";
import DesignationsAdmin from "@/pages/admin/DesignationsAdmin";
import ImamsAdmin from "@/pages/admin/ImamsAdmin";
import AnnouncementsAdmin from "@/pages/admin/AnnouncementsAdmin";
import IqamahAdmin from "@/pages/admin/IqamahAdmin";
import PrayerAnnouncementAdmin from "@/pages/admin/PrayerAnnouncementAdmin";
import NewsletterAdmin from "@/pages/admin/NewsletterAdmin";
import DonationsAdmin from "@/pages/admin/DonationsAdmin";
import MessagesAdmin from "@/pages/admin/MessagesAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import ReportsAdmin from "@/pages/admin/ReportsAdmin";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import ContactRequestTypesAdmin from "@/pages/admin/ContactRequestTypesAdmin";
import FacilityRentalsAdmin from "@/pages/admin/FacilityRentalsAdmin";
import ImpactStatsAdmin from "@/pages/admin/ImpactStatsAdmin";
import StorageCleanupAdmin from "@/pages/admin/StorageCleanupAdmin";
import CommunityResourcesAdmin from "@/pages/admin/CommunityResourcesAdmin";
import TeamAdmin from "@/pages/admin/TeamAdmin";

import "@/App.css";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageLoader />
        <RouteProgress />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/facility-rentals" element={<FacilityRentals />} />
            <Route path="/facility-rentals" element={<FacilityRentals />} />
            <Route path="/services/message-of-submission" element={<MessageOfSubmission />} />
            <Route path="/message-of-submission" element={<MessageOfSubmission />} />
            <Route path="/community-resources" element={<CommunityResources />} />
            <Route path="/p/community-resources" element={<CommunityResources />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/donate/success" element={<DonateSuccess />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/prayer-times" element={<PrayerTimesPage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/p/membership" element={<Membership />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/p/:slug" element={<DynamicPage />} />
            <Route path="/gallery" element={<Gallery />} />
            {/* Catch-all "clean" slug — must be LAST so hard-coded routes win.
                e.g. /summer → DynamicPage looks up the "summer" page from the API.
                Reserved slugs (events, about, donate, etc.) are blocked at admin
                create-time so they can't collide here. */}
            <Route path="/:slug" element={<DynamicPage />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="events" element={<EventsAdmin />} />
            <Route path="services" element={<ServicesAdmin />} />
            <Route path="pages" element={<PagesAdmin />} />
            <Route path="imams" element={<ImamsAdmin />} />
            <Route path="announcements" element={<AnnouncementsAdmin />} />
            <Route path="iqamah" element={<IqamahAdmin />} />
            <Route path="prayer-card" element={<PrayerAnnouncementAdmin />} />
            <Route path="newsletter" element={<NewsletterAdmin />} />
            <Route path="donations" element={<DonationsAdmin />} />
            <Route path="designations" element={<DesignationsAdmin />} />
            <Route path="messages" element={<MessagesAdmin />} />
            <Route path="reports" element={<ReportsAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
            <Route path="gallery" element={<GalleryAdmin />} />
            <Route path="facility-rentals" element={<FacilityRentalsAdmin />} />
            <Route path="contact-types" element={<ContactRequestTypesAdmin />} />
            <Route path="impact-stats" element={<ImpactStatsAdmin />} />
            <Route path="storage" element={<StorageCleanupAdmin />} />
            <Route path="community-resources" element={<CommunityResourcesAdmin />} />
            <Route path="team" element={<TeamAdmin />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
